import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const JenniferGenesis = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.35, 0.7, 1],
    ['grayscale(100%) brightness(0.35)', 'grayscale(60%) brightness(0.6)', 'grayscale(20%) brightness(0.85)', 'grayscale(0%) brightness(1)']
  );

  return (
    <section ref={ref} className="relative bg-dark border-t border-white/5 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[100vh]">
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden order-2 lg:order-1">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/c8e44d59a4e812fc499ebd3f1cc275f0.png"
            alt="Jennifer Gräser"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent" />
          <motion.div
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.06), transparent 55%)' }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-10 left-6 right-12 p-6 z-10"
            style={{ background: 'rgba(5,5,5,0.75)', backdropFilter: 'blur(10px)', border: '1px solid rgba(212,175,55,0.2)' }}
          >
            <p className="text-base font-serif italic text-cream/80 leading-relaxed">
              "Ich bin diesen Weg durchs Feuer gegangen –{' '}
              <span className="text-gold">damit du nicht mehr brennen musst.</span>"
            </p>
          </motion.div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-8 md:px-14 py-28 space-y-8 order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gold tracking-[0.3em] uppercase text-xs font-semibold"
          >
            Jennifers Genesis
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight"
          >
            Vom tiefsten Schmerz{' '}
            <span className="italic text-gold block mt-2">zur absoluten Wiedergeburt.</span>
          </motion.h2>

          {[
            { delay: 0.2, text: 'Es gab einen Moment in meinem Leben, in dem ich auf dem Badezimmerboden saß und nicht wusste, wie ich weitermachen sollte. Ich hatte alles gegeben – in einer Liebe, die mich aushöhlte, und in einem Business, das mich erschöpfte. Ich funktionierte nach außen hin perfekt. Innen war ich leer.' },
            { delay: 0.4, highlight: true, text: 'Das war mein Moment der absoluten Kapitulation. Und gleichzeitig: meine Wiedergeburt.' },
            { delay: 0.6, text: 'In den folgenden Jahren habe ich jede Methode, jedes System, jede spirituelle Praxis erkundet – nicht aus akademischem Interesse, sondern aus purer Überlebensnot. Ich wollte verstehen, warum ich mich immer wieder in denselben Mustern wiederfand. Warum Liebe sich für mich immer wie ein Kampf anfühlte. Warum Erfolg mich nie wirklich erfüllte.' },
            { delay: 0.8, text: 'Die Antwort fand ich nicht in einem Buch. Ich fand sie in mir. In dem Moment, als ich aufhörte, mich zu reparieren, und anfing, mich zu lieben. Als ich aufhörte zu rennen und anfing, zu empfangen. Als ich die maskuline Rüstung ablegte und entdeckte, dass meine feminine Essenz meine stärkste Kraft ist.' },
            { delay: 1.0, gold: true, text: 'Heute begleite ich Frauen auf diesem Weg. Nicht als Expertin, die über ihnen steht. Sondern als Frau, die jeden Schritt dieses Feuers kennt – und weiß, was am anderen Ende wartet: Freiheit, die sich wie Heimkommen anfühlt.' },
          ].map((block, i) => (
            <motion.p key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${block.gold ? 'text-gold font-serif italic text-xl' : block.highlight ? 'text-cream font-serif italic text-2xl' : 'text-cream/65'}`}
            >
              {block.text}
            </motion.p>
          ))}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.2 }}
            className="pt-6 border-t border-white/10"
          >
            <p className="text-cream font-semibold tracking-widest uppercase text-sm">Jennifer Gräser</p>
            <p className="text-gold text-xs uppercase tracking-widest mt-1">Gründerin · Coaching Female</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JenniferGenesis;